import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state, query } from 'lit/decorators.js';
import { baseStyles } from '../styles/shared.js';
import { treeStyles } from '../styles/tree-styles.js';
import type { ModelerController } from '../controllers/modeler-controller.js';
import type { DocumentController, CursorState } from '../controllers/document-controller.js';
import type { LayoutController } from '../controllers/layout-controller.js';
import type { TabInfo } from './sysml-tab-bar.js';
import type { EditorGroupState } from './sysml-editor-zone.js';
import type { PaletteCommand } from './sysml-command-palette.js';
import type { ChatbotController } from '../controllers/chatbot-controller.js';
import type { IngestionController } from '../controllers/ingestion-controller.js';
import type { IngestionJournal } from '../api/ingestion-types.js';
import type {
  LayoutStateV2,
  ActivityBarItem,
  BottomPanelTab,
  SecondarySidebarTab,
  LayoutPresetName,
} from '../utils/constants.js';
import {
  EXPLORER_MIN_WIDTH,
  EXPLORER_MAX_WIDTH,
  DEFAULT_EXPLORER_WIDTH,
  SECONDARY_SIDEBAR_MIN_WIDTH,
  SECONDARY_SIDEBAR_MAX_WIDTH,
  DEFAULT_SECONDARY_SIDEBAR_WIDTH,
  BOTTOM_PANEL_MIN_HEIGHT,
  BOTTOM_PANEL_MAX_HEIGHT,
  DEFAULT_BOTTOM_PANEL_HEIGHT,
  PARSE_DEBOUNCE_MS,
  STORAGE_KEYS,
  LAYOUT_PRESETS,
  SECONDARY_SIDEBAR_TABS,
} from '../utils/constants.js';
import { loadLayoutV2, saveLayoutDebouncedV2 } from '../utils/layout-persistence.js';
import { emitDocumentSaved, emitParseComplete, emitDocumentChanged } from '../utils/events.js';
import { modelToTreeNodes } from '../utils/model-tree.js';
import type { TreeNode } from '../utils/model-tree.js';

// Register Stencil web components from @arctek/sysml-studio
import '@arctek/sysml-studio/loader';

// Import child Lit components
import './sysml-toolbar.js';
import './sysml-resize-handle.js';
import './sysml-activity-bar.js';
import './sysml-status-bar.js';
import './sysml-panel-zone.js';
import './sysml-secondary-sidebar.js';
import './sysml-chatbot-panel.js';
import './sysml-layout-renderer.js';
import './sysml-health-panel.js';
import './sysml-verification-panel.js';
import './sysml-traceability-panel.js';
import './sysml-requirements-panel.js';
import './sysml-builder-wizard.js';
import './sysml-tab-bar.js';
import './sysml-editor-group.js';
import './sysml-editor-zone.js';
import './sysml-file-explorer.js';
import './sysml-ingest-panel.js';
import './sysml-solver-panel.js';
import './sysml-diff-history-panel.js';
import './sysml-simulation-panel.js';
import './sysml-command-palette.js';
import './sysml-audit-trail.js';
import './sysml-breadcrumbs.js';

// ---------------------------------------------------------------------------
// <sysml-workspace> -- V2 Layout Orchestrator (VS Code zone model)
//
// +------------------------------------------------------------------+
// | <sysml-toolbar>  (simplified)                                    |
// +------+--------+---------------------+---------+------+-----------+
// | Act. | Primary|   Editor            | Diagram | Rh   | Secondary |
// | Bar  | Side-  |   <sysml-editor>    | <sysml- |      | Sidebar   |
// | 48px | bar    |                     |  diagram|      |           |
// |      |        |   <-- resize -->    |  >      |      | - Chat    |
// |      | Explor |                     |         |      | - Health  |
// |      | Search |-----resize-handle---|         |      | - Verify  |
// |      | Tree   |   <panel-zone>      |         |      | - Reqs    |
// |      |        |   diagnostics|search|         |      | - Builder |
// +------+--------+---------------------+---------+------+-----------+
// | <sysml-status-bar>  24px                                         |
// +------------------------------------------------------------------+
// ---------------------------------------------------------------------------

/** Detect macOS for keyboard shortcuts */
const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform);

@customElement('sysml-workspace')
export class SysmlWorkspace extends LitElement {
  static override styles = [
    baseStyles,
    treeStyles,
    css`
      :host {
        display: flex;
        flex-direction: column;
        width: 100%;
        height: 100%;
        overflow: hidden;
        background: var(--r5-surface);
      }
      :host(.fullscreen) {
        position: fixed;
        inset: 0;
        z-index: 1000;
      }

      sysml-breadcrumbs {
        border-bottom: 1px solid var(--r5-border);
        background: var(--r5-surface-muted);
        flex-shrink: 0;
      }

      .main-area {
        display: flex;
        flex: 1;
        overflow: hidden;
      }

      /* -- Primary Sidebar -- */
      .primary-sidebar {
        min-width: 0;
        flex-shrink: 0;
        border-right: 1px solid var(--r5-border);
        overflow: hidden;
        display: flex;
        flex-direction: column;
        transition: width var(--r5-duration-normal) var(--r5-ease-default);
        background: var(--r5-bg, var(--r5-surface));
        position: relative;
        z-index: 1;
      }
      .primary-sidebar.collapsed { width: 0 !important; border-right: none; }
      :host(.resizing) .primary-sidebar { transition: none; }

      /* -- Center area -- */
      .center-area {
        display: flex;
        flex-direction: column;
        flex: 1;
        min-width: 0;
        overflow: hidden;
        position: relative;
        z-index: 0;
      }

      /* -- Editor + Diagram split -- */
      .editor-diagram-area {
        display: flex;
        flex: 1;
        min-width: 0;
        overflow: hidden;
      }
      .editor-pane {
        min-width: 0;
        overflow: hidden;
        display: flex;
        flex-direction: column;
      }
      .diagram-pane {
        flex: 1;
        min-width: 0;
        overflow: hidden;
        display: flex;
        flex-direction: column;
        border-left: 1px solid var(--r5-border);
      }
      .diagram-pane-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        height: 35px;
        padding: 0 var(--r5-space-2) 0 var(--r5-space-3);
        background: var(--r5-surface-muted, var(--r5-surface));
        border-bottom: 1px solid var(--r5-border);
        flex-shrink: 0;
        font-size: var(--r5-text-xs);
        color: var(--r5-text-muted);
        text-transform: uppercase;
        letter-spacing: 0.05em;
      }
      .diagram-pane-header button {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 22px;
        height: 22px;
        border: none;
        background: transparent;
        color: var(--r5-text-muted);
        cursor: pointer;
        border-radius: var(--r5-radius-sm);
        padding: 0;
      }
      .diagram-pane-header button:hover {
        background: color-mix(in srgb, var(--r5-text-muted) 15%, transparent);
        color: var(--r5-text);
      }
      .restore-diagram-strip {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 24px;
        flex-shrink: 0;
        background: var(--r5-surface-muted, var(--r5-surface));
        border-left: 1px solid var(--r5-border);
        cursor: pointer;
        color: var(--r5-text-muted);
        writing-mode: vertical-rl;
        font-size: var(--r5-text-xs);
        letter-spacing: 0.03em;
        user-select: none;
      }
      .restore-diagram-strip:hover {
        background: color-mix(in srgb, var(--r5-primary) 10%, transparent);
        color: var(--r5-primary);
      }

      /* -- Decomposition Breadcrumbs -- */
      .decomposition-breadcrumbs {
        display: flex;
        align-items: center;
        gap: 2px;
        padding: 4px var(--r5-space-2);
        background: var(--r5-surface-muted);
        border-bottom: 1px solid var(--r5-border);
        font-size: var(--r5-text-xs);
        flex-shrink: 0;
        overflow-x: auto;
      }
      .decomposition-crumb {
        padding: 2px 6px;
        border: none;
        background: transparent;
        color: var(--r5-text-muted);
        cursor: pointer;
        border-radius: var(--r5-radius-sm);
        font-size: inherit;
        white-space: nowrap;
      }
      .decomposition-crumb:hover:not(:disabled) {
        background: color-mix(in srgb, var(--r5-text) 8%, transparent);
        color: var(--r5-text);
      }
      .decomposition-crumb.active {
        color: var(--r5-text);
        font-weight: 500;
        cursor: default;
      }
      .decomposition-sep {
        color: var(--r5-text-muted);
        opacity: 0.5;
      }
      .decomposition-up {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 20px;
        height: 20px;
        margin-left: 4px;
        border: none;
        background: transparent;
        color: var(--r5-text-muted);
        cursor: pointer;
        border-radius: var(--r5-radius-sm);
        padding: 0;
      }
      .decomposition-up:hover {
        background: color-mix(in srgb, var(--r5-text) 8%, transparent);
        color: var(--r5-text);
      }

      /* -- Secondary Activity Bar (always-visible icon strip) -- */
      .secondary-activity-bar {
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 36px;
        flex-shrink: 0;
        border-left: 1px solid var(--r5-border);
        background: var(--r5-surface-muted);
        padding-top: var(--r5-space-1);
        overflow-y: auto;
        overflow-x: hidden;
      }
      .sa-icon-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 30px;
        height: 30px;
        border: none;
        border-radius: var(--r5-radius-sm);
        background: none;
        color: var(--r5-text-muted);
        cursor: pointer;
        transition: all var(--r5-duration-fast) var(--r5-ease-default);
        margin-bottom: 2px;
        padding: 0;
      }
      .sa-icon-btn:hover {
        color: var(--r5-text);
        background: color-mix(in srgb, var(--r5-text) 8%, transparent);
      }
      .sa-icon-btn.active {
        color: var(--r5-primary);
        background: color-mix(in srgb, var(--r5-primary) 12%, transparent);
      }

      /* -- Secondary Sidebar -- */
      .secondary-sidebar-wrapper {
        flex-shrink: 0;
        min-width: 0;
        overflow: hidden;
        transition: width var(--r5-duration-normal) var(--r5-ease-default);
        position: relative;
        z-index: 1;
      }
      .secondary-sidebar-wrapper.collapsed { width: 0 !important; }
      :host(.resizing) .secondary-sidebar-wrapper { transition: none; }

      /* -- Bottom panel zone -- */
      .panel-zone-wrapper {
        flex-shrink: 0;
        overflow: hidden;
        transition: height var(--r5-duration-normal) var(--r5-ease-default);
      }
      .panel-zone-wrapper.collapsed { height: 0 !important; }
      :host(.resizing) .panel-zone-wrapper { transition: none; }

      /* Stencil WC hosts take full space */
      sysml-layout-renderer {
        display: block;
        flex: 1;
        min-width: 0;
        min-height: 0;
      }

      sysml-editor, sysml-diagram, sysml-explorer, sysml-search,
      sysml-diagnostics, sysml-ibd, sysml-sequence-diagram, sysml-state-machine,
      sysml-activity-diagram, sysml-package-diagram, sysml-requirement-diagram,
      sysml-use-case-diagram, sysml-parametric-diagram,
      sysml-quick-ref, sysml-history, sysml-tree,
      sysml-simulation-controls, sysml-simulation-trace, sysml-simulation-state {
        display: block;
        width: 100%;
        height: 100%;
      }

      /* -- Context menu overlay -- */
      .context-menu-backdrop {
        position: fixed;
        inset: 0;
        z-index: 100;
      }
      .context-menu {
        position: fixed;
        z-index: 101;
        min-width: 160px;
        background: var(--r5-surface-overlay, var(--r5-surface));
        border: 1px solid var(--r5-border);
        border-radius: var(--r5-radius-md);
        box-shadow: var(--r5-shadow-lg, 0 8px 24px rgba(0,0,0,.25));
        padding: var(--r5-space-1) 0;
        list-style: none;
        margin: 0;
      }
      .context-menu li {
        display: flex;
        align-items: center;
        gap: var(--r5-space-2);
        padding: var(--r5-space-1) var(--r5-space-3);
        font-size: var(--r5-text-xs);
        color: var(--r5-text);
        cursor: pointer;
        white-space: nowrap;
      }
      .context-menu li:hover {
        background: color-mix(in srgb, var(--r5-primary) 12%, transparent);
      }
      .context-menu li.destructive {
        color: var(--r5-destructive-700);
      }
      .context-menu-sep {
        height: 1px;
        background: var(--r5-border);
        margin: var(--r5-space-1) 0;
      }

      /* -- Layout progress indicator -- */
      .layout-progress {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 2px;
        z-index: 10;
        overflow: hidden;
      }
      .layout-progress-bar {
        height: 100%;
        width: 40%;
        background: var(--r5-primary);
        animation: layout-slide 1.2s ease-in-out infinite;
      }
      @keyframes layout-slide {
        0% { transform: translateX(-100%); }
        100% { transform: translateX(350%); }
      }
    `,
  ];

  // Controllers use a generation counter
  @property({ attribute: false }) modeler!: ModelerController;
  @property({ attribute: false }) documents!: DocumentController;
  @property({ attribute: false }) chatbot!: ChatbotController;
  @property({ attribute: false }) ingestion!: IngestionController;
  @property({ attribute: false }) layout!: LayoutController;

  /** Primitive generation counter — triggers re-render when the modeler mutates */
  @property({ attribute: false, type: Number }) generation = 0;

  /** Primitive generation counter for workspace layout reactivity */
  @property({ attribute: false, type: Number }) layoutGeneration = 0;

  /** Primitive generation counter for ingestion controller reactivity */
  @property({ attribute: false, type: Number }) ingestionGeneration = 0;

  // --- V2 layout state (persisted) ---
  @state() private _layout: LayoutStateV2 = loadLayoutV2();

  // --- Non-persisted UI state ---
  @state() private _isFullscreen = false;
  @state() private _useNewLayout = true;
  @state() private _contextMenu: { nodeId: string; x: number; y: number } | null = null;
  @state() private _theme: 'light' | 'dark' = (() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.THEME);
      if (stored === 'light' || stored === 'dark') return stored;
    } catch { /* ignore */ }
    if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: light)').matches) {
      return 'light';
    }
    return 'dark';
  })();
  @state() private _searchResults: Array<{
    id: string; type: string; name: string; path: string;
    line?: number; column?: number; preview?: string; score: number; tags?: string[];
  }> = [];

  /** Editor zone groups — default 2 groups (editor + diagram) */
  @state() private _editorGroups: EditorGroupState[] = [
    { id: 'editor', sizePct: this._layout?.editorSplitPct ?? 50 },
    { id: 'diagram', sizePct: 100 - (this._layout?.editorSplitPct ?? 50) },
  ];
  @state() private _focusedGroup = 'editor';

  /** Tracks which panels were auto-collapsed by the responsive observer */
  private _autoCollapsed = new Set<'secondary' | 'bottom' | 'primary'>();

  /** Command palette open/close */
  @state() private _commandPaletteOpen = false;

  /** Audit trail overlay state */
  @state() private _auditTrailOpen = false;
  @state() private _auditTrailJournal: IngestionJournal | null = null;

  /** Model tree state — maintained across toggles, rebuilt on new parse */
  @state() private _modelTreeNodes: TreeNode[] = [];
  private _lastParsedModelRef: unknown = null;

  @query('.primary-sidebar') private _primarySidebarEl!: HTMLElement;

  private _boundKeyHandler = this._onKeyDown.bind(this);
  private _boundAuditTrailHandler = this._onOpenAuditTrail.bind(this);
  private _resizeObserver: ResizeObserver | null = null;

  /** Merge partial layout update and persist.
   *  When a collapse/expand changes happen, the CSS transition takes ~200ms.
   *  After that completes, we notify Monaco and diagrams to re-layout by
   *  dispatching a window resize event. */
  private _updateLayout(partial: Partial<LayoutStateV2>): void {
    const collapseChanged =
      partial.primarySidebarCollapsed !== undefined ||
      partial.secondarySidebarCollapsed !== undefined ||
      partial.bottomPanelCollapsed !== undefined;
    this._layout = { ...this._layout, ...partial };
    if (collapseChanged) {
      // Fire after CSS transition ends (~250ms) to trigger internal ResizeObservers
      setTimeout(() => window.dispatchEvent(new Event('resize')), 260);
    }
  }

  override connectedCallback(): void {
    super.connectedCallback();
    this._applyTheme(this._theme);
    document.addEventListener('keydown', this._boundKeyHandler);
    this.addEventListener('open-audit-trail', this._boundAuditTrailHandler as EventListener);

    // Responsive breakpoints — auto-collapse/restore zones at width thresholds
    this._resizeObserver = new ResizeObserver((entries) => {
      const { width } = entries[0].contentRect;

      // Secondary sidebar: collapse < 800, restore >= 800
      if (width < 800 && !this._layout.secondarySidebarCollapsed) {
        this._autoCollapsed.add('secondary');
        this._updateLayout({ secondarySidebarCollapsed: true });
      } else if (width >= 800 && this._autoCollapsed.has('secondary')) {
        this._autoCollapsed.delete('secondary');
        this._updateLayout({ secondarySidebarCollapsed: false });
      }

      // Bottom panel: collapse < 600, restore >= 600
      if (width < 600 && !this._layout.bottomPanelCollapsed) {
        this._autoCollapsed.add('bottom');
        this._updateLayout({ bottomPanelCollapsed: true });
      } else if (width >= 600 && this._autoCollapsed.has('bottom')) {
        this._autoCollapsed.delete('bottom');
        this._updateLayout({ bottomPanelCollapsed: false });
      }

      // Primary sidebar: collapse < 480, restore >= 480
      if (width < 480 && !this._layout.primarySidebarCollapsed) {
        this._autoCollapsed.add('primary');
        this._updateLayout({ primarySidebarCollapsed: true });
      } else if (width >= 480 && this._autoCollapsed.has('primary')) {
        this._autoCollapsed.delete('primary');
        this._updateLayout({ primarySidebarCollapsed: false });
      }
    });
    this._resizeObserver.observe(this);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this._boundKeyHandler);
    this.removeEventListener('open-audit-trail', this._boundAuditTrailHandler as EventListener);
    this._resizeObserver?.disconnect();
    this._resizeObserver = null;
  }

  override updated(): void {
    saveLayoutDebouncedV2(this._layout);

    if (this._useNewLayout && this.layout && this.documents.currentDocument) {
      const hasTabs = this._hasAnyLayoutTabs(this.layout.layout.root);
      if (!hasTabs) {
        const doc = this.documents.currentDocument;
        this.layout.openTab({
          id: `tab-${doc.id}`,
          documentId: doc.id,
          viewType: 'editor',
          label: doc.name,
          isDirty: this.modeler.isDirty,
          isPinned: false,
        });
      }
    }

    // Rebuild model tree nodes when the parsed model changes
    const currentModel = this.modeler.parsedModel;
    if (currentModel !== this._lastParsedModelRef) {
      this._lastParsedModelRef = currentModel;
      this._modelTreeNodes = modelToTreeNodes(currentModel);
    }
  }

  private _hasAnyLayoutTabs(node: { type: string; tabs?: unknown[]; children?: [any, any] }): boolean {
    if (node.type === 'pane') {
      return Array.isArray(node.tabs) && node.tabs.length > 0;
    }
    if (node.type === 'split' && Array.isArray(node.children)) {
      return this._hasAnyLayoutTabs(node.children[0]) || this._hasAnyLayoutTabs(node.children[1]);
    }
    return false;
  }


  // -- Keyboard Shortcuts ---------------------------------------------------

  private _onKeyDown(e: KeyboardEvent): void {
    if (e.defaultPrevented) return;

    // Escape — close audit trail first, then command palette, then context menu, then exit focus mode
    if (e.key === 'Escape') {
      if (this._auditTrailOpen) {
        e.preventDefault();
        this._auditTrailOpen = false;
        this._auditTrailJournal = null;
        return;
      }
      if (this._commandPaletteOpen) {
        e.preventDefault();
        this._commandPaletteOpen = false;
        return;
      }
      if (this._contextMenu) {
        e.preventDefault();
        this._contextMenu = null;
        return;
      }
      if (this._layout.focusMode !== 'none') {
        e.preventDefault();
        this._updateLayout({ focusMode: 'none' });
        return;
      }
    }

    // Alt+Up — navigate up in decomposition (BDD or IBD)
    if (e.key === 'ArrowUp' && e.altKey && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
      if (this._layout.diagramView === 'ibd' && this.modeler.decompositionStack.length > 0) {
        e.preventDefault();
        this.modeler.navigateUpDecomposition();
        return;
      }
      if (this._layout.diagramView === 'diagram' && this.modeler.bddScopePartDefId) {
        e.preventDefault();
        this.modeler.navigateUpBDD();
        return;
      }
    }

    const mod = IS_MAC ? e.metaKey : e.ctrlKey;
    if (!mod) return;

    if (e.key === 'p' && e.shiftKey) {
      // Cmd/Ctrl+Shift+P — open command palette
      e.preventDefault();
      this._commandPaletteOpen = !this._commandPaletteOpen;
    } else if (e.key === 'b' && !e.shiftKey) {
      // Cmd/Ctrl+B — toggle primary sidebar
      e.preventDefault();
      this._updateLayout({ primarySidebarCollapsed: !this._layout.primarySidebarCollapsed });
    } else if (e.key === 'B' || (e.key === 'b' && e.shiftKey)) {
      // Cmd/Ctrl+Shift+B — toggle secondary sidebar
      e.preventDefault();
      this._updateLayout({ secondarySidebarCollapsed: !this._layout.secondarySidebarCollapsed });
    } else if (e.key === 'j' && !e.shiftKey) {
      // Cmd/Ctrl+J — toggle bottom panel
      e.preventDefault();
      this._updateLayout({ bottomPanelCollapsed: !this._layout.bottomPanelCollapsed });
    } else if (e.key === 'f' && e.shiftKey) {
      // Cmd/Ctrl+Shift+F — open search in bottom panel
      e.preventDefault();
      this._updateLayout({ bottomPanelCollapsed: false, bottomPanelActiveTab: 'search' });
    } else if (e.key === 'w' && !e.shiftKey) {
      // Cmd/Ctrl+W — close active tab
      e.preventDefault();
      if (this._useNewLayout && this.layout) {
        // New layout system: close active tab in active pane
        const activePane = this.findActivePane(this.layout.layout.root);
        if (activePane?.activeTabId) {
          this.layout.closeTab(activePane.activeTabId);
        }
      } else if (this.documents.activeDocumentId) {
        // Old system: close document tab
        this._onTabClose(new CustomEvent('tab-close', {
          detail: { id: this.documents.activeDocumentId },
        }));
      }
    } else if (e.key === '\\' && e.shiftKey) {
      // Cmd/Ctrl+Shift+\ — split active pane vertically
      e.preventDefault();
      if (this._useNewLayout && this.layout) {
        this.layout.splitPane(this.layout.layout.activePaneId, 'vertical');
      }
    } else if (e.key === '\\') {
      // Cmd/Ctrl+\ — split active pane horizontally
      e.preventDefault();
      if (this._useNewLayout && this.layout) {
        this.layout.splitPane(this.layout.layout.activePaneId, 'horizontal');
      } else {
        this._onSplitEditor();
      }
    } else if (e.key === 'Tab' && e.ctrlKey && !e.metaKey) {
      // Ctrl+Tab / Ctrl+Shift+Tab — cycle tabs
      e.preventDefault();
      this._saveCursorForActiveDoc();
      const doc = this.documents.cycleTab(e.shiftKey ? 'prev' : 'next');
      if (doc) {
        this.modeler.loadSourceText(doc.content);
        this._restoreCursorForDoc(doc.id);
      }
      this.requestUpdate();
    } else if (e.key >= '1' && e.key <= '4') {
      // Cmd/Ctrl+1/2/3/4 — focus editor group by index
      e.preventDefault();
      const idx = parseInt(e.key) - 1;
      if (idx < this._editorGroups.length) {
        this._focusedGroup = this._editorGroups[idx].id;
      }
    }
  }

  private findActivePane(node: any): any {
    if (node.type === 'pane') {
      return node;
    }
    const left = this.findActivePane(node.children[0]);
    if (left?.id === this.layout?.layout.activePaneId) return left;
    const right = this.findActivePane(node.children[1]);
    if (right?.id === this.layout?.layout.activePaneId) return right;
    return left || right;
  }

  // -- Resize helpers -------------------------------------------------------

  private _onResizeStart(): void {
    this.classList.add('resizing');
  }

  private _onResizeEnd(): void {
    this.classList.remove('resizing');
  }

  // -- Render ---------------------------------------------------------------

  override render() {
    const L = this._layout;

    return html`
      <sysml-toolbar
        .modeler=${this.modeler}
        .documents=${this.documents}
        .diagramView=${L.diagramView}
        ?diagram-visible=${L.focusMode !== 'editor'}
        @save=${this._onSave}
        @diagram-view-change=${(e: CustomEvent<string>) => {
          const view = e.detail as LayoutStateV2['diagramView'];
          // Auto-select IBD target when switching to IBD view without one
          if (view === 'ibd' && !this.modeler.activeIBDProjection) {
            this.modeler.autoSelectIBDTarget();
          }
          // If diagram is hidden, re-open the split when a view type is clicked
          if (L.focusMode === 'editor') {
            this._updateLayout({ diagramView: view, focusMode: 'none' });
          } else {
            this._updateLayout({ diagramView: view });
          }
        }}
        @auto-layout=${() => this.modeler.autoLayout()}
        @format=${this._onFormat}
        @layout-algorithm-change=${(e: CustomEvent<{ algorithm: string }>) =>
          this.modeler.updateLayoutOptions({ algorithm: e.detail.algorithm as 'layered' | 'stress' | 'mrtree' | 'force' })}
        @layout-direction-change=${(e: CustomEvent<{ direction: string }>) =>
          this.modeler.updateLayoutOptions({ direction: e.detail.direction as 'RIGHT' | 'DOWN' })}
        @diagram-export=${(e: CustomEvent<{ format: 'svg' | 'png' }>) =>
          this.modeler.exportDiagram(e.detail.format)}
      ></sysml-toolbar>

      <!-- Breadcrumbs bar -->
      <sysml-breadcrumbs
        .documentName=${this.documents.currentDocumentName}
        .elementPath=${this.modeler.getElementPathAtCursor()}
        @breadcrumb-navigate=${this._onBreadcrumbNavigate}
      ></sysml-breadcrumbs>

      <div class="main-area">
        <!-- Activity Bar -->
        <sysml-activity-bar
          .activeItem=${L.primarySidebarActiveItem}
          ?sidebar-collapsed=${L.primarySidebarCollapsed}
          @activity-item-click=${this._onActivityItemClick}
          @settings-click=${this._onSettingsClick}
        ></sysml-activity-bar>

        <!-- Primary Sidebar -->
        ${this._renderPrimarySidebar()}

        <!-- Primary Sidebar Resize Handle -->
        ${!L.primarySidebarCollapsed
          ? html`
              <sysml-resize-handle
                mode="pixel"
                direction="start"
                min-px=${EXPLORER_MIN_WIDTH}
                max-px=${EXPLORER_MAX_WIDTH}
                default-px=${DEFAULT_EXPLORER_WIDTH}
                .container=${this._primarySidebarEl}
                @resize=${(e: CustomEvent<{ px: number }>) =>
                  this._updateLayout({ primarySidebarWidth: e.detail.px })}
                @collapse=${() => this._updateLayout({ primarySidebarCollapsed: true })}
                @resize-start=${this._onResizeStart}
                @resize-end=${this._onResizeEnd}
              ></sysml-resize-handle>
            `
          : nothing}

        <!-- Center Area -->
        ${this._renderCenterArea()}

        <!-- Secondary Sidebar Resize Handle -->
        ${!L.secondarySidebarCollapsed
          ? html`
              <sysml-resize-handle
                mode="pixel"
                direction="end"
                min-px=${SECONDARY_SIDEBAR_MIN_WIDTH}
                max-px=${SECONDARY_SIDEBAR_MAX_WIDTH}
                default-px=${DEFAULT_SECONDARY_SIDEBAR_WIDTH}
                .container=${this as HTMLElement}
                @resize=${(e: CustomEvent<{ px: number }>) =>
                  this._updateLayout({ secondarySidebarWidth: e.detail.px })}
                @collapse=${() => this._updateLayout({ secondarySidebarCollapsed: true })}
                @resize-start=${this._onResizeStart}
                @resize-end=${this._onResizeEnd}
              ></sysml-resize-handle>
            `
          : nothing}

        <!-- Secondary Sidebar (content only — icon bar rendered externally) -->
        <div
          class="secondary-sidebar-wrapper ${L.secondarySidebarCollapsed ? 'collapsed' : ''}"
          style=${L.secondarySidebarCollapsed ? '' : `width: ${L.secondarySidebarWidth}px`}
        >
          <sysml-secondary-sidebar
            hide-icon-bar
            .modeler=${this.modeler}
            .documents=${this.documents}
            .chatbot=${this.chatbot}
            .ingestion=${this.ingestion}
            .ingestionGeneration=${this.ingestionGeneration}
            .activeTab=${L.secondarySidebarActiveTab}
            .theme=${this._theme}
            @tab-change=${(e: CustomEvent<{ tab: SecondarySidebarTab }>) =>
              this._onSecondaryTabChange(e.detail.tab)}
            @insert-code=${this._onInsertCode}
            @apply-suggestion=${this._onApplySuggestion}
            @quick-ref-insert=${this._onQuickRefInsert}
          ></sysml-secondary-sidebar>
        </div>

        <!-- Secondary Activity Bar — always visible icon strip -->
        <div class="secondary-activity-bar" role="tablist" aria-label="Panel tabs" aria-orientation="vertical">
          ${SECONDARY_SIDEBAR_TABS.map(
            (tab) => html`
              <ark-tooltip content=${tab.label} placement="left">
                <button
                  class="sa-icon-btn ${!L.secondarySidebarCollapsed && L.secondarySidebarActiveTab === tab.id ? 'active' : ''}"
                  role="tab"
                  aria-selected=${!L.secondarySidebarCollapsed && L.secondarySidebarActiveTab === tab.id ? 'true' : 'false'}
                  aria-label=${tab.label}
                  @click=${() => this._onSecondaryTabChange(tab.id as SecondarySidebarTab)}
                >
                  <ark-icon name=${tab.icon} size="sm"></ark-icon>
                </button>
              </ark-tooltip>
            `,
          )}
        </div>
      </div>

      <!-- Status Bar -->
      <sysml-status-bar
        .modeler=${this.modeler}
        .theme=${this._theme}
        @toggle-diagnostics=${this._onToggleDiagnostics}
        @toggle-theme=${this._onToggleTheme}
      ></sysml-status-bar>

      <!-- Command Palette -->
      <sysml-command-palette
        .commands=${this._getPaletteCommands()}
        ?open=${this._commandPaletteOpen}
        @command-execute=${this._onCommandExecute}
        @palette-close=${() => { this._commandPaletteOpen = false; }}
      ></sysml-command-palette>

      <!-- Audit Trail Overlay -->
      <sysml-audit-trail
        .journal=${this._auditTrailJournal}
        ?open=${this._auditTrailOpen}
        @audit-trail-close=${() => { this._auditTrailOpen = false; this._auditTrailJournal = null; }}
      ></sysml-audit-trail>

      <!-- Context Menu Overlay -->
      ${this._contextMenu
        ? (() => {
            const ctxNodeId = this._contextMenu!.nodeId;
            const ctxEl = this.modeler.parsedModel?.allElements.find(e => e.id === ctxNodeId);
            const ctxType = ctxEl?.elementType;
            const showIBD = ctxType === 'partDef' || ctxType === 'partUsage';
            const showStateMachine = this.modeler.stateMachineProjections.some(p => p.contextElementId === ctxNodeId);
            const showActivity = this.modeler.activityProjections.some(p => p.contextElementId === ctxNodeId);
            const showSequence = this.modeler.sequenceProjections.some(p => p.contextElementId === ctxNodeId);
            const hasViewOptions = showIBD || showStateMachine || showActivity || showSequence;
            return html`
            <div class="context-menu-backdrop" @click=${this._closeContextMenu} @contextmenu=${(e: Event) => { e.preventDefault(); this._closeContextMenu(); }}></div>
            <ul class="context-menu" style="left: ${this._contextMenu.x}px; top: ${this._contextMenu.y}px;">
              <li @click=${() => this._contextMenuAction('open')}>
                <ark-icon name="file-text" size="sm"></ark-icon>
                Open in Editor
              </li>
              ${showIBD ? html`
              <li @click=${() => this._contextMenuAction('ibd')}>
                <ark-icon name="git-branch" size="sm"></ark-icon>
                View IBD
              </li>
              ` : nothing}
              ${showStateMachine ? html`
              <li @click=${() => this._contextMenuAction('statemachine')}>
                <ark-icon name="git-commit" size="sm"></ark-icon>
                View State Machine
              </li>
              ` : nothing}
              ${showActivity ? html`
              <li @click=${() => this._contextMenuAction('activity')}>
                <ark-icon name="activity" size="sm"></ark-icon>
                View Activity
              </li>
              ` : nothing}
              ${showSequence ? html`
              <li @click=${() => this._contextMenuAction('sequence')}>
                <ark-icon name="align-left" size="sm"></ark-icon>
                View Sequence
              </li>
              ` : nothing}
              ${hasViewOptions ? html`<div class="context-menu-sep"></div>` : nothing}
              <li @click=${() => this._contextMenuAction('connect')}>
                <ark-icon name="link" size="sm"></ark-icon>
                Add Connection
              </li>
              <div class="context-menu-sep"></div>
              <li class="destructive" @click=${() => this._contextMenuAction('delete')}>
                <ark-icon name="trash-2" size="sm"></ark-icon>
                Delete
              </li>
            </ul>
          `;
          })()
        : nothing}
    `;
  }

  // -- Primary Sidebar ------------------------------------------------------

  private _renderPrimarySidebar() {
    const L = this._layout;
    return html`
      <div
        class="primary-sidebar ${L.primarySidebarCollapsed ? 'collapsed' : ''}"
        style=${L.primarySidebarCollapsed ? '' : `width: ${L.primarySidebarWidth}px`}
      >
        ${this._renderPrimarySidebarContent()}
      </div>
    `;
  }

  private _renderPrimarySidebarContent() {
    switch (this._layout.primarySidebarActiveItem) {
      case 'explorer':
        return html`
          <sysml-file-explorer
            .nodes=${this.documents.explorerNodes}
            .selectedId=${this.documents.currentDocument?.id ?? ''}
            @fileSelect=${(e: CustomEvent) => this._onFileSelect(e)}
            @fileCreate=${(e: CustomEvent) => this._onFileCreate(e)}
          ></sysml-file-explorer>
        `;
      case 'search':
        return html`
          <sysml-search
            .searchResults=${this._searchResults}
            @searchQuery=${(e: CustomEvent<{ query: string; filters: Array<{ tag: string; active: boolean }> }>) =>
              this._onSearchQuery(e.detail.query, e.detail.filters)}
            @resultSelect=${(e: CustomEvent) =>
              this._onSearchResultSelect(e.detail)}
          ></sysml-search>
        `;
      case 'model-tree':
        return html`
          <sysml-tree
            .nodes=${this._modelTreeNodes}
            @nodeSelect=${(e: CustomEvent<TreeNode>) => {
              const elementId = (e.detail?.data?.elementId as string) ?? e.detail?.id;
              if (elementId) this._onElementOpen(elementId);
            }}
            @folderToggle=${this._onModelTreeToggle}
            @sysmlContextMenu=${(e: CustomEvent<{ target: { kind: string; docId?: string; folderId?: string }; x: number; y: number }>) => {
              const nodeId = e.detail.target.docId ?? e.detail.target.folderId;
              if (nodeId) this._onNodeContextMenu({ nodeId, x: e.detail.x, y: e.detail.y });
            }}
          ></sysml-tree>
        `;
    }
  }

  // -- Center Area ----------------------------------------------------------

  private _renderEditor() {
    return html`
      <sysml-editor
        .value=${this.modeler.sourceText}
        .theme=${this._theme}
        .parseDelay=${PARSE_DEBOUNCE_MS}
        .externalDiagnostics=${this.modeler.diagnostics}
        @valueChange=${(e: CustomEvent<string>) => {
          const editor = e.target as HTMLElement & { lastChangeRange?: { startLine: number; endLine: number; lineDelta: number } | null };
          const changeRange = editor?.lastChangeRange ?? undefined;
          this.modeler.updateSourceText(e.detail, changeRange);
          // Editing pins the preview tab
          if (this.documents.activeDocumentId) {
            this.documents.pinDocument(this.documents.activeDocumentId);
          }
        }}
        @sysmlParsed=${(e: CustomEvent) => this._onEditorParsed(e)}
        @cursorChange=${(e: CustomEvent<{ line: number; column: number; offset: number }>) => {
          this.modeler.handleCursorChange(e.detail);
          this._syncCursorToSelection(e.detail);
        }}
        @saveRequest=${() => this._onSave()}
      ></sysml-editor>
    `;
  }

  private _getDiagramTypeLabel(): string {
    const labels: Record<string, string> = {
      diagram: 'Block Diagram',
      ibd: 'Internal Block Diagram',
      sequence: 'Sequence Diagram',
      statemachine: 'State Machine',
      activity: 'Activity Diagram',
      package: 'Package Diagram',
      requirement: 'Requirement Diagram',
      usecase: 'Use Case Diagram',
      parametric: 'Parametric Diagram',
    };
    const label = labels[this._layout.diagramView];
    return label ? ` \u2014 ${label}` : '';
  }

  private _renderDiagram() {
    const view = this._layout.diagramView;
    if (view === 'ibd') {
      return html`
        ${this._renderDecompositionBreadcrumbs()}
        <sysml-ibd
          .projection=${this.modeler.activeIBDProjection}
          .showGrid=${true}
          .theme=${this._theme}
          @partSelect=${(e: CustomEvent<string>) => this._onIBDPartSelect(e.detail)}
          @partOpen=${(e: CustomEvent<string>) => this._onIBDPartOpen(e.detail)}
          @portSelect=${(e: CustomEvent<string>) => this._onIBDPortSelect(e.detail)}
          @connectionSelect=${(e: CustomEvent<string>) => this._onIBDConnectionSelect(e.detail)}
        ></sysml-ibd>
      `;
    }
    if (view === 'sequence') {
      return html`
        <sysml-sequence-diagram
          .projection=${this.modeler.activeSequenceProjection}
          .theme=${this._theme}
          @lifelineSelect=${(e: CustomEvent<string>) => this._onSequenceLifelineSelect(e.detail)}
          @messageSelect=${(e: CustomEvent<string>) => this._onSequenceMessageSelect(e.detail)}
          @lifelineOpen=${(e: CustomEvent<string>) => this._onElementOpen(e.detail)}
          @selectionClear=${() => this.modeler.clearSelection()}
        ></sysml-sequence-diagram>
      `;
    }
    if (view === 'statemachine') {
      return html`
        <sysml-state-machine
          .projection=${this.modeler.activeStateMachineProjection}
          .activeStateId=${this.modeler.activeSimulationStateId}
          .theme=${this._theme}
          @stateSelect=${(e: CustomEvent<string>) => this._onStateMachineStateSelect(e.detail)}
          @transitionSelect=${(e: CustomEvent<string>) => this._onStateMachineTransitionSelect(e.detail)}
          @stateOpen=${(e: CustomEvent<string>) => this._onElementOpen(e.detail)}
          @selectionClear=${() => this.modeler.clearSelection()}
        ></sysml-state-machine>
      `;
    }
    if (view === 'activity') {
      return html`
        <sysml-activity-diagram
          .projection=${this.modeler.activeActivityProjection}
          .activeActionId=${this.modeler.activeSimulationActionId}
          .theme=${this._theme}
          @nodeSelect=${(e: CustomEvent<string>) => this._onActivityNodeSelect(e.detail)}
          @edgeSelect=${(e: CustomEvent<string>) => this._onActivityEdgeSelect(e.detail)}
          @nodeOpen=${(e: CustomEvent<string>) => this._onElementOpen(e.detail)}
          @selectionClear=${() => this.modeler.clearSelection()}
        ></sysml-activity-diagram>
      `;
    }
    if (view === 'package') {
      return html`
        <sysml-package-diagram
          .projection=${this.modeler.activePackageProjection}
          .theme=${this._theme}
          @nodeSelect=${(e: CustomEvent<string>) => this._onPackageNodeSelect(e.detail)}
          @edgeSelect=${(e: CustomEvent<string>) => this._onPackageEdgeSelect(e.detail)}
          @nodeOpen=${(e: CustomEvent<string>) => this._onElementOpen(e.detail)}
          @selectionClear=${() => this.modeler.clearSelection()}
        ></sysml-package-diagram>
      `;
    }
    if (view === 'requirement') {
      return html`
        <sysml-requirement-diagram
          .projection=${this.modeler.activeRequirementProjection}
          .theme=${this._theme}
          @nodeSelect=${(e: CustomEvent<string>) => this._onRequirementNodeSelect(e.detail)}
          @edgeSelect=${(e: CustomEvent<string>) => this._onRequirementEdgeSelect(e.detail)}
          @nodeOpen=${(e: CustomEvent<string>) => this._onElementOpen(e.detail)}
          @selectionClear=${() => this.modeler.clearSelection()}
        ></sysml-requirement-diagram>
      `;
    }
    if (view === 'usecase') {
      return html`
        <sysml-use-case-diagram
          .projection=${this.modeler.activeUseCaseProjection}
          .theme=${this._theme}
          @nodeSelect=${(e: CustomEvent<string>) => this._onUseCaseNodeSelect(e.detail)}
          @edgeSelect=${(e: CustomEvent<string>) => this._onUseCaseEdgeSelect(e.detail)}
          @nodeOpen=${(e: CustomEvent<string>) => this._onElementOpen(e.detail)}
          @selectionClear=${() => this.modeler.clearSelection()}
        ></sysml-use-case-diagram>
      `;
    }
    if (view === 'parametric') {
      return html`
        <sysml-parametric-diagram
          .projection=${this.modeler.activeParametricProjection}
          .theme=${this._theme}
          @nodeSelect=${(e: CustomEvent<string>) => this._onParametricNodeSelect(e.detail)}
          @edgeSelect=${(e: CustomEvent<string>) => this._onParametricEdgeSelect(e.detail)}
          @nodeOpen=${(e: CustomEvent<string>) => this._onElementOpen(e.detail)}
          @selectionClear=${() => this.modeler.clearSelection()}
        ></sysml-parametric-diagram>
      `;
    }
    return html`
      ${this._renderBDDBreadcrumbs()}
      <sysml-diagram
        .nodes=${this.modeler.diagramNodes}
        .edges=${this.modeler.diagramEdges}
        .partDefinitions=${this.modeler.partDefinitions}
        .selectedNodeIds=${this.modeler.selectedNodeIds}
        .selectedEdgeIds=${this.modeler.selectedEdgeIds}
        .viewport=${this.modeler.viewport}
        .showGrid=${false}
        .theme=${this._theme}
        @nodeSelect=${(e: CustomEvent<string>) => this._onDiagramNodeSelect(e.detail)}
        @edgeSelect=${(e: CustomEvent<string>) => this.modeler.selectEdge(e.detail)}
        @selectionClear=${() => this.modeler.clearSelection()}
        @nodePositionChange=${(e: CustomEvent<{ nodeId: string; x: number; y: number }>) =>
          this.modeler.updateNodePosition({ id: e.detail.nodeId, x: e.detail.x, y: e.detail.y })}
        @viewportChange=${(e: CustomEvent) => this.modeler.updateViewport(e.detail)}
        @elementOpen=${(e: CustomEvent<string>) => this._onBlockDiagramElementOpen(e.detail)}
        @nodeContextMenu=${(e: CustomEvent<{ nodeId: string; x: number; y: number }>) =>
          this._onNodeContextMenu(e.detail)}
      ></sysml-diagram>
    `;
  }

  private _renderCenterArea() {
    const L = this._layout;

    // New layout system — uses layout renderer for tabbed editor panes,
    // but renders diagrams directly via sysml-editor-zone (not as tabs).
    if (this._useNewLayout && this.layout) {
      return html`
        <div class="center-area">
          <sysml-editor-zone
            .groups=${this._editorGroups}
            focused-group=${this._focusedGroup}
            focus-mode=${L.focusMode}
            @groups-resize=${(e: CustomEvent<{ groups: EditorGroupState[] }>) => {
              this._editorGroups = e.detail.groups;
              const editorGroup = e.detail.groups.find((g) => g.id === 'editor');
              if (editorGroup) this._updateLayout({ editorSplitPct: editorGroup.sizePct });
            }}
            @group-focus=${(e: CustomEvent<{ groupId: string }>) => {
              this._focusedGroup = e.detail.groupId;
            }}
            @focus-mode-change=${(e: CustomEvent<{ mode: string }>) => {
              this._updateLayout({ focusMode: e.detail.mode as LayoutStateV2['focusMode'] });
            }}
          >
            <!-- Editor group (tabbed pane layout) -->
            <div slot="group-editor" class="editor-pane" style="display: flex; flex-direction: column;">
              <sysml-layout-renderer
                .layout=${this.layout.layout}
                .layoutController=${this.layout}
                .documentController=${this.documents}
                .modeler=${this.modeler}
                .onSaveRequest=${() => this._onSave()}
                .theme=${this._theme}
              ></sysml-layout-renderer>
            </div>

            <!-- Diagram group -->
            <div slot="group-diagram" class="diagram-pane" style="display: flex; flex-direction: column; position: relative;">
              <div class="diagram-pane-header">
                <span>Diagram${this._getDiagramTypeLabel()}</span>
                <div style="display:flex;align-items:center;gap:2px;">
                  <button
                    title=${L.focusMode === 'diagram' ? 'Restore split view' : 'Maximize diagram'}
                    @click=${() => this._updateLayout({ focusMode: L.focusMode === 'diagram' ? 'none' : 'diagram' })}
                  >
                    ${L.focusMode === 'diagram'
                      ? html`<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="4 14 4 20 10 20"/><polyline points="20 10 20 4 14 4"/><line x1="14" y1="10" x2="21" y2="3"/><line x1="3" y1="21" x2="10" y2="14"/></svg>`
                      : html`<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>`}
                  </button>
                  <button
                    title="Close diagram panel"
                    @click=${() => this._updateLayout({ focusMode: 'editor' })}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </button>
                </div>
              </div>
              ${this.modeler.layoutInProgress
                ? html`<div class="layout-progress"><div class="layout-progress-bar"></div></div>`
                : nothing}
              ${this._renderDiagram()}
            </div>
          </sysml-editor-zone>

          <!-- Restore diagram strip (visible when diagram is hidden) -->
          ${L.focusMode === 'editor'
            ? html`
                <div
                  class="restore-diagram-strip"
                  title="Restore diagram panel"
                  @click=${() => this._updateLayout({ focusMode: 'none' })}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-bottom: 4px;"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="12" y1="3" x2="12" y2="21"/></svg>
                </div>
              `
            : nothing}

          <!-- Bottom Panel Resize Handle -->
          ${!L.bottomPanelCollapsed
            ? html`
                <sysml-resize-handle
                  mode="pixel"
                  orientation="horizontal"
                  direction="end"
                  min-px=${BOTTOM_PANEL_MIN_HEIGHT}
                  max-px=${BOTTOM_PANEL_MAX_HEIGHT}
                  default-px=${DEFAULT_BOTTOM_PANEL_HEIGHT}
                  @resize=${(e: CustomEvent<{ px: number }>) =>
                    this._updateLayout({ bottomPanelHeight: e.detail.px })}
                  @collapse=${() => this._updateLayout({ bottomPanelCollapsed: true })}
                  @resize-start=${this._onResizeStart}
                  @resize-end=${this._onResizeEnd}
                ></sysml-resize-handle>
              `
            : nothing}

          <!-- Bottom Panel Zone -->
          <div
            class="panel-zone-wrapper ${L.bottomPanelCollapsed ? 'collapsed' : ''}"
            style=${L.bottomPanelCollapsed ? '' : `height: ${L.bottomPanelHeight}px`}
          >
            <sysml-panel-zone
              .modeler=${this.modeler}
              .activeTab=${L.bottomPanelActiveTab}
              .theme=${this._theme}
              .searchResults=${this._searchResults}
              @tab-change=${(e: CustomEvent<{ tab: BottomPanelTab }>) =>
                this._updateLayout({ bottomPanelActiveTab: e.detail.tab })}
              @panel-close=${() => this._updateLayout({ bottomPanelCollapsed: true })}
              @search-query=${(e: CustomEvent<{ query: string; filters: Array<{ tag: string; active: boolean }> }>) =>
                this._onSearchQuery(e.detail.query, e.detail.filters)}
              @search-result-select=${(e: CustomEvent) =>
                this._onSearchResultSelect(e.detail)}
              @diagnostic-reveal=${(e: CustomEvent) =>
                this._revealRange(e.detail)}
            ></sysml-panel-zone>
          </div>
        </div>
      `;
    }

    // Original editor-zone implementation
    return html`
      <div class="center-area">
        <sysml-editor-zone
          .groups=${this._editorGroups}
          focused-group=${this._focusedGroup}
          focus-mode=${L.focusMode}
          @groups-resize=${(e: CustomEvent<{ groups: EditorGroupState[] }>) => {
            this._editorGroups = e.detail.groups;
            // Sync editor split pct from first two groups
            const editorGroup = e.detail.groups.find((g) => g.id === 'editor');
            if (editorGroup) this._updateLayout({ editorSplitPct: editorGroup.sizePct });
          }}
          @group-focus=${(e: CustomEvent<{ groupId: string }>) => {
            this._focusedGroup = e.detail.groupId;
          }}
          @focus-mode-change=${(e: CustomEvent<{ mode: string }>) => {
            this._updateLayout({ focusMode: e.detail.mode as LayoutStateV2['focusMode'] });
          }}
        >
          <!-- Editor group -->
          <div slot="group-editor" class="editor-pane" style="display: flex; flex-direction: column;">
            <sysml-tab-bar
              .tabs=${this._getTabInfos()}
              active-tab-id=${this.documents.activeDocumentId ?? ''}
              @tab-select=${this._onTabSelect}
              @tab-close=${this._onTabClose}
              @tab-pin=${this._onTabPin}
              @tab-reorder=${this._onTabReorder}
            ></sysml-tab-bar>
            ${this._renderEditor()}
          </div>

          <!-- Diagram group -->
          <div slot="group-diagram" class="diagram-pane" style="display: flex; flex-direction: column; position: relative;">
            <div class="diagram-pane-header">
              <span>Diagram${this._getDiagramTypeLabel()}</span>
              <div style="display:flex;align-items:center;gap:2px;">
                <button
                  title=${L.focusMode === 'diagram' ? 'Restore split view' : 'Maximize diagram'}
                  @click=${() => this._updateLayout({ focusMode: L.focusMode === 'diagram' ? 'none' : 'diagram' })}
                >
                  ${L.focusMode === 'diagram'
                    ? html`<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="4 14 4 20 10 20"/><polyline points="20 10 20 4 14 4"/><line x1="14" y1="10" x2="21" y2="3"/><line x1="3" y1="21" x2="10" y2="14"/></svg>`
                    : html`<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>`}
                </button>
                <button
                  title="Close diagram panel"
                  @click=${() => this._updateLayout({ focusMode: 'editor' })}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              </div>
            </div>
            ${this.modeler.layoutInProgress
              ? html`<div class="layout-progress"><div class="layout-progress-bar"></div></div>`
              : nothing}
            ${this._renderDiagram()}
          </div>
        </sysml-editor-zone>

        <!-- Restore diagram strip (visible when diagram is hidden) -->
        ${L.focusMode === 'editor'
          ? html`
              <div
                class="restore-diagram-strip"
                title="Restore diagram panel"
                @click=${() => this._updateLayout({ focusMode: 'none' })}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-bottom: 4px;"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="12" y1="3" x2="12" y2="21"/></svg>
              </div>
            `
          : nothing}

        <!-- Bottom Panel Resize Handle -->
        ${!L.bottomPanelCollapsed
          ? html`
              <sysml-resize-handle
                mode="pixel"
                orientation="horizontal"
                direction="end"
                min-px=${BOTTOM_PANEL_MIN_HEIGHT}
                max-px=${BOTTOM_PANEL_MAX_HEIGHT}
                default-px=${DEFAULT_BOTTOM_PANEL_HEIGHT}
                @resize=${(e: CustomEvent<{ px: number }>) =>
                  this._updateLayout({ bottomPanelHeight: e.detail.px })}
                @collapse=${() => this._updateLayout({ bottomPanelCollapsed: true })}
                @resize-start=${this._onResizeStart}
                @resize-end=${this._onResizeEnd}
              ></sysml-resize-handle>
            `
          : nothing}

        <!-- Bottom Panel Zone -->
        <div
          class="panel-zone-wrapper ${L.bottomPanelCollapsed ? 'collapsed' : ''}"
          style=${L.bottomPanelCollapsed ? '' : `height: ${L.bottomPanelHeight}px`}
        >
          <sysml-panel-zone
            .modeler=${this.modeler}
            .activeTab=${L.bottomPanelActiveTab}
            .theme=${this._theme}
            .searchResults=${this._searchResults}
            @tab-change=${(e: CustomEvent<{ tab: BottomPanelTab }>) =>
              this._updateLayout({ bottomPanelActiveTab: e.detail.tab })}
            @panel-close=${() => this._updateLayout({ bottomPanelCollapsed: true })}
            @search-query=${(e: CustomEvent<{ query: string; filters: Array<{ tag: string; active: boolean }> }>) =>
              this._onSearchQuery(e.detail.query, e.detail.filters)}
            @search-result-select=${(e: CustomEvent) =>
              this._onSearchResultSelect(e.detail)}
            @diagnostic-reveal=${(e: CustomEvent) =>
              this._revealRange(e.detail)}
          ></sysml-panel-zone>
        </div>
      </div>
    `;
  }

  // -- Context Menu ---------------------------------------------------------

  private _onNodeContextMenu(detail: { nodeId: string; x: number; y: number }): void {
    this._contextMenu = { nodeId: detail.nodeId, x: detail.x, y: detail.y };
  }

  private _closeContextMenu(): void {
    this._contextMenu = null;
  }

  private _contextMenuAction(action: 'open' | 'ibd' | 'statemachine' | 'activity' | 'sequence' | 'connect' | 'delete'): void {
    const nodeId = this._contextMenu?.nodeId;
    this._contextMenu = null;
    if (!nodeId) return;

    switch (action) {
      case 'open':
        this._onElementOpen(nodeId);
        break;
      case 'ibd': {
        // Resolve the node to its part definition and select as IBD target.
        // If the node IS a part def, use it directly.
        // If it's a part usage, resolve its typeRef to the corresponding part def.
        // Otherwise, fall back to auto-select.
        const model = this.modeler.parsedModel;
        if (model) {
          const el = model.allElements.find(e => e.id === nodeId);
          if (el?.elementType === 'partDef') {
            this.modeler.selectIBDTarget(nodeId);
          } else if (el?.elementType === 'partUsage') {
            const typeRef = (el as unknown as { typeRef?: string })?.typeRef;
            const targetDef = typeRef && typeRef !== '?'
              ? model.allPartDefs.find(pd => pd.id === typeRef || pd.name === typeRef || pd.qualifiedName === typeRef)
              : undefined;
            if (targetDef) {
              this.modeler.selectIBDTarget(targetDef.id);
            } else {
              this.modeler.autoSelectIBDTarget();
            }
          } else {
            this.modeler.autoSelectIBDTarget();
          }
        }
        this._updateLayout({ diagramView: 'ibd' });
        break;
      }
      case 'statemachine':
        this.modeler.selectStateMachineByElementId(nodeId);
        this._updateLayout({ diagramView: 'statemachine' });
        break;
      case 'activity':
        this.modeler.selectActivityByElementId(nodeId);
        this._updateLayout({ diagramView: 'activity' });
        break;
      case 'sequence':
        this.modeler.selectSequenceProjectionByElementId(nodeId);
        this._updateLayout({ diagramView: 'sequence' });
        break;
      case 'connect':
        // Select the node — connection creation is handled by the diagram component
        this.modeler.selectNode(nodeId);
        break;
      case 'delete': {
        const el = this.modeler.allElements.find((e) => e.id === nodeId);
        if (el?.source) {
          this.modeler.deleteSourceRange(el.source);
        }
        break;
      }
    }
  }

  // -- Activity Bar ---------------------------------------------------------

  private _onActivityItemClick(e: CustomEvent<{ item: ActivityBarItem }>): void {
    const item = e.detail.item;
    if (this._layout.primarySidebarActiveItem === item && !this._layout.primarySidebarCollapsed) {
      // Clicking active item → collapse sidebar
      this._updateLayout({ primarySidebarCollapsed: true });
    } else {
      // Switch to this item and ensure sidebar is open
      this._updateLayout({ primarySidebarActiveItem: item, primarySidebarCollapsed: false });
    }
  }

  private _onSettingsClick(): void {
    this._commandPaletteOpen = true;
  }

  // -- Audit Trail -----------------------------------------------------------

  private _onOpenAuditTrail(e: CustomEvent<{ journal: IngestionJournal }>): void {
    this._auditTrailJournal = e.detail.journal;
    this._auditTrailOpen = true;
  }

  // -- Model Tree Toggle -----------------------------------------------------

  private _onModelTreeToggle = (e: CustomEvent<string>): void => {
    const nodeId = e.detail;
    const toggle = (nodes: TreeNode[]): boolean => {
      for (const n of nodes) {
        if (n.id === nodeId) {
          n.isOpen = !n.isOpen;
          return true;
        }
        if (n.children && toggle(n.children)) return true;
      }
      return false;
    };
    toggle(this._modelTreeNodes);
    this.requestUpdate();
  };

  // -- Secondary Sidebar Tab Change -----------------------------------------

  private _onSecondaryTabChange(tab: SecondarySidebarTab): void {
    if (this._layout.secondarySidebarActiveTab === tab) {
      // Clicking active tab → collapse
      this._updateLayout({ secondarySidebarCollapsed: true });
    } else {
      this._updateLayout({ secondarySidebarActiveTab: tab, secondarySidebarCollapsed: false });
    }
  }

  // -- Editor Splits ---------------------------------------------------------

  private _onSplitEditor(): void {
    const zone = this.shadowRoot?.querySelector('sysml-editor-zone') as
      | (HTMLElement & { splitGroup: (groupId: string, newGroupId: string) => EditorGroupState[] })
      | null;
    if (!zone) return;

    const newId = `split-${Date.now()}`;
    const newGroups = zone.splitGroup(this._focusedGroup, newId);
    if (newGroups !== this._editorGroups) {
      this._editorGroups = newGroups;
      this._focusedGroup = newId;
    }
  }

  // -- Editor Tabs -----------------------------------------------------------

  /** Build TabInfo[] for the tab bar from the document controller's open tabs */
  private _getTabInfos(): TabInfo[] {
    return this.documents.openTabs.map((tab) => {
      const doc = this.documents.documents.find((d) => d.id === tab.documentId);
      return {
        id: tab.documentId,
        name: doc?.name ?? 'Untitled',
        isDirty: tab.documentId === this.documents.activeDocumentId && this.modeler.isDirty,
        isPreview: tab.isPreview,
      };
    });
  }

  private _onTabSelect(e: CustomEvent<{ id: string }>): void {
    const id = e.detail.id;
    if (id === this.documents.activeDocumentId) return;

    // Save cursor state for current doc before switching
    this._saveCursorForActiveDoc();

    const doc = this.documents.openDocument(id);
    if (doc) {
      this.modeler.loadSourceText(doc.content);
      this._restoreCursorForDoc(id);
    }
    this.requestUpdate();
  }

  private _onTabClose(e: CustomEvent<{ id: string }>): void {
    const closingId = e.detail.id;
    const wasActive = closingId === this.documents.activeDocumentId;

    const newActiveId = this.documents.closeDocument(closingId);

    if (wasActive && newActiveId) {
      const doc = this.documents.documents.find((d) => d.id === newActiveId);
      if (doc) {
        this.modeler.loadSourceText(doc.content);
        this._restoreCursorForDoc(newActiveId);
      }
    } else if (!newActiveId) {
      // No tabs left — clear editor
      this.modeler.loadSourceText('');
    }
    this.requestUpdate();
  }

  private _onTabPin(e: CustomEvent<{ id: string }>): void {
    this.documents.pinDocument(e.detail.id);
    this.requestUpdate();
  }

  private _onTabReorder(e: CustomEvent<{ sourceId: string; targetId: string }>): void {
    this.documents.reorderTabs(e.detail.sourceId, e.detail.targetId);
    this.requestUpdate();
  }

  /** Save current cursor position before switching tabs */
  private _saveCursorForActiveDoc(): void {
    const activeId = this.documents.activeDocumentId;
    if (!activeId) return;
    const cursor = this.modeler.cursorPosition;
    if (cursor) {
      this.documents.saveCursorPosition(activeId, {
        line: cursor.line,
        column: cursor.column,
      });
    }
  }

  /** Restore cursor position after switching to a tab */
  private _restoreCursorForDoc(docId: string): void {
    const saved = this.documents.getCursorPosition(docId);
    if (saved) {
      // Defer to allow editor to update value first
      requestAnimationFrame(() => {
        this._revealRange({
          startLine: saved.line,
          startColumn: saved.column,
          endLine: saved.line,
          endColumn: saved.column,
        });
      });
    }
  }

  // -- Toggle Handlers ------------------------------------------------------

  private _onToggleDiagnostics(): void {
    if (!this._layout.bottomPanelCollapsed && this._layout.bottomPanelActiveTab === 'diagnostics') {
      this._updateLayout({ bottomPanelCollapsed: true });
    } else {
      this._updateLayout({ bottomPanelCollapsed: false, bottomPanelActiveTab: 'diagnostics' });
    }
  }

  private _onToggleTheme(): void {
    const next = this._theme === 'dark' ? 'light' : 'dark';
    this._theme = next;
    this._applyTheme(next);
  }

  /** Apply theme class/attribute to host, MFE parent, and document for design-system token switching */
  private _applyTheme(theme: 'light' | 'dark'): void {
    try { localStorage.setItem(STORAGE_KEYS.THEME, theme); } catch { /* ignore */ }

    // Apply to self (<sysml-workspace>)
    if (theme === 'dark') {
      this.classList.add('dark');
      this.setAttribute('data-theme', 'dark');
    } else {
      this.classList.remove('dark');
      this.setAttribute('data-theme', 'light');
    }

    // Apply to <sysml-mfe> host — its :host(.dark) selector defines CSS custom property overrides
    const mfeHost = (this.getRootNode() as ShadowRoot)?.host;
    if (mfeHost) {
      if (theme === 'dark') {
        mfeHost.classList.add('dark');
        mfeHost.setAttribute('data-theme', 'dark');
      } else {
        mfeHost.classList.remove('dark');
        mfeHost.setAttribute('data-theme', 'light');
      }
    }

    // Apply to document root for global CSS (design-system tokens)
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
      root.setAttribute('data-theme', 'dark');
    } else {
      root.classList.remove('dark');
      root.setAttribute('data-theme', 'light');
    }
  }

  // -- Event Handlers (preserved from V1) -----------------------------------

  private async _onSave(): Promise<void> {
    await this.documents.saveCurrentDocument(this.modeler.sourceText);
    this.modeler.markClean();
    emitDocumentSaved(this, {
      documentId: this.documents.currentDocument?.id ?? '',
      name: this.documents.currentDocumentName,
    });
  }

  private _onFileSelect(e: CustomEvent): void {
    const detail = e.detail;
    const id = typeof detail === 'string' ? detail : detail?.id;
    if (!id) return;

    // Save cursor for current doc before switching
    this._saveCursorForActiveDoc();

    // Single-click in explorer → preview tab
    const doc = this.documents.openDocument(id, /* preview */ true);
    if (doc) {
      if (this._useNewLayout && this.layout) {
        this.layout.openTab({
          id: `tab-${doc.id}`,
          documentId: doc.id,
          viewType: 'editor',
          label: doc.name,
          isDirty: this.modeler.isDirty,
          isPinned: false,
        });
      }
      this.modeler.loadSourceText(doc.content);
      this._restoreCursorForDoc(id);
      emitDocumentChanged(this, { documentId: doc.id, name: doc.name });
    }
    this.requestUpdate();
  }

  private async _onFileCreate(e: CustomEvent): Promise<void> {
    const doc = await this.documents.createDocument(e.detail);
    this.modeler.loadSourceText(doc.content);
    
    // Also open in new layout system
    if (this._useNewLayout && this.layout) {
      this.layout.openTab({
        id: `tab-${doc.id}`,
        documentId: doc.id,
        viewType: 'editor',
        label: doc.name,
        isDirty: false,
        isPinned: false,
      }, undefined, false);
    }
    
    this.requestUpdate();
  }

  private _onEditorParsed(_e: CustomEvent): void {
    emitParseComplete(this, {
      success: this.modeler.parseSuccess,
      partCount: this.modeler.partDefinitions.length,
      errorCount: this.modeler.errorCount,
      warningCount: this.modeler.warningCount,
    });

    if (this.chatbot.completenessScore !== null) {
      const ctx = this.chatbot.buildModelContext(this.modeler);
      this.chatbot.refreshQualityScore(ctx);
    }
  }

  /** Editor cursor moved → highlight the closest diagram node.
   *  Only syncs when the element under cursor changes to avoid churn. */
  private _lastCursorElementId: string | null = null;
  private _syncCursorToSelection(pos: { line: number; column: number }): void {
    // Find the most specific (smallest) element containing the cursor
    const match = this.modeler.allElements
      .filter((el) => {
        const s = el.source;
        if (!s) return false;
        if (pos.line < s.startLine || pos.line > s.endLine) return false;
        if (pos.line === s.startLine && pos.column < s.startColumn) return false;
        if (pos.line === s.endLine && pos.column > s.endColumn) return false;
        return true;
      })
      .sort((a, b) => {
        // Smallest range = most specific
        const sizeA = (a.source!.endLine - a.source!.startLine) * 1000 + (a.source!.endColumn - a.source!.startColumn);
        const sizeB = (b.source!.endLine - b.source!.startLine) * 1000 + (b.source!.endColumn - b.source!.startColumn);
        return sizeA - sizeB;
      })[0];

    // Find the corresponding diagram node (walk up ancestors if needed)
    const nodeIds = new Set(this.modeler.diagramNodes.map((n) => n.id));
    let elementId = match?.id ?? null;

    // If the element isn't a diagram node, walk up the element path to find one
    if (elementId && !nodeIds.has(elementId)) {
      const path = this.modeler.getElementPathAtCursor();
      for (let i = path.length - 1; i >= 0; i--) {
        if (nodeIds.has(path[i].id)) {
          elementId = path[i].id;
          break;
        }
      }
    }

    if (elementId !== this._lastCursorElementId) {
      this._lastCursorElementId = elementId;
      if (elementId && nodeIds.has(elementId)) {
        this.modeler.selectedNodeIds = [elementId];
        this.modeler.selectedEdgeIds = [];
      }
    }
  }

  /** Diagram node selected → select in modeler + reveal source in editor */
  private _onDiagramNodeSelect(nodeId: string): void {
    this.modeler.selectNode(nodeId);
    // Reveal the element's source range in the editor
    const el = this.modeler.allElements.find((e) => e.id === nodeId);
    if (el?.source) {
      this._revealRange(el.source);
    }
  }

  private _onElementOpen(elementId: string): void {
    const el = this.modeler.allElements.find((e) => e.id === elementId);
    if (el?.source) {
      this._revealRange(el.source);
    }
  }

  /** Block diagram double-click: scope BDD into the part def's children */
  private _onBlockDiagramElementOpen(elementId: string): void {
    const el = this.modeler.allElements.find((e) => e.id === elementId);
    if (!el) return;

    // If this is a part definition with sub-parts, scope the BDD (stay in BDD view)
    if (el.elementType === 'partDef' && this.modeler.parsedModel) {
      const partDef = this.modeler.parsedModel.allPartDefs.find(pd => pd.id === elementId);
      if (partDef && partDef.parts.length > 0) {
        this.modeler.scopeBDD(elementId);
        return;
      }
    }

    // If this is a state definition with nested states, scope into it
    if (el.elementType === 'stateDef' && this.modeler.parsedModel) {
      const stateDef = this.modeler.parsedModel.allStateDefs.find(sd => sd.id === elementId);
      if (stateDef && ((stateDef.states?.length ?? 0) + (stateDef.transitions?.length ?? 0)) > 0) {
        this.modeler.scopeBDD(elementId);
        return;
      }
    }

    // Fallback: reveal in editor
    if (el.source) {
      this._revealRange(el.source);
    }
  }

  // -- IBD event handlers ---------------------------------------------------

  private _onIBDPartSelect(partId: string): void {
    this.modeler.selectNode(partId);
    const el = this.modeler.allElements.find((e) => e.id === partId);
    if (el?.source) {
      this._revealRange(el.source);
    }
  }

  private _onIBDPartOpen(partId: string): void {
    // Try to drill deeper — if the part's type has sub-parts, decompose
    this.modeler.drillIntoIBD(partId);
  }

  private _onIBDPortSelect(portId: string): void {
    const el = this.modeler.allElements.find((e) => e.id === portId);
    if (el?.source) {
      this._revealRange(el.source);
    }
  }

  private _onIBDConnectionSelect(connectionId: string): void {
    const el = this.modeler.allElements.find((e) => e.id === connectionId);
    if (el?.source) {
      this._revealRange(el.source);
    }
  }

  // -- Sequence diagram event handlers --------------------------------------

  private _onSequenceLifelineSelect(lifelineId: string): void {
    this.modeler.selectNode(lifelineId);
    const el = this.modeler.allElements.find((e) => e.id === lifelineId);
    if (el?.source) {
      this._revealRange(el.source);
    }
  }

  private _onSequenceMessageSelect(messageId: string): void {
    const el = this.modeler.allElements.find((e) => e.id === messageId);
    if (el?.source) {
      this._revealRange(el.source);
    }
  }

  // -- State machine diagram event handlers ---------------------------------

  private _onStateMachineStateSelect(stateId: string): void {
    this.modeler.selectNode(stateId);
    const el = this.modeler.allElements.find((e) => e.id === stateId);
    if (el?.source) {
      this._revealRange(el.source);
    }
  }

  private _onStateMachineTransitionSelect(transitionId: string): void {
    const el = this.modeler.allElements.find((e) => e.id === transitionId);
    if (el?.source) {
      this._revealRange(el.source);
    }
  }

  // -- Activity diagram event handlers --------------------------------------

  private _onActivityNodeSelect(nodeId: string): void {
    this.modeler.selectNode(nodeId);
    const el = this.modeler.allElements.find((e) => e.id === nodeId);
    if (el?.source) {
      this._revealRange(el.source);
    }
  }

  private _onActivityEdgeSelect(edgeId: string): void {
    const el = this.modeler.allElements.find((e) => e.id === edgeId);
    if (el?.source) {
      this._revealRange(el.source);
    }
  }

  // -- Package diagram event handlers ----------------------------------------

  private _onPackageNodeSelect(nodeId: string): void {
    this.modeler.selectNode(nodeId);
    const el = this.modeler.allElements.find((e) => e.id === nodeId);
    if (el?.source) {
      this._revealRange(el.source);
    }
  }

  private _onPackageEdgeSelect(edgeId: string): void {
    const el = this.modeler.allElements.find((e) => e.id === edgeId);
    if (el?.source) {
      this._revealRange(el.source);
    }
  }

  // -- Requirement diagram event handlers ------------------------------------

  private _onRequirementNodeSelect(nodeId: string): void {
    this.modeler.selectNode(nodeId);
    const el = this.modeler.allElements.find((e) => e.id === nodeId);
    if (el?.source) {
      this._revealRange(el.source);
    }
  }

  private _onRequirementEdgeSelect(edgeId: string): void {
    const el = this.modeler.allElements.find((e) => e.id === edgeId);
    if (el?.source) {
      this._revealRange(el.source);
    }
  }

  // -- Use case diagram event handlers ---------------------------------------

  private _onUseCaseNodeSelect(nodeId: string): void {
    this.modeler.selectNode(nodeId);
    const el = this.modeler.allElements.find((e) => e.id === nodeId);
    if (el?.source) {
      this._revealRange(el.source);
    }
  }

  private _onUseCaseEdgeSelect(edgeId: string): void {
    const el = this.modeler.allElements.find((e) => e.id === edgeId);
    if (el?.source) {
      this._revealRange(el.source);
    }
  }

  // -- Parametric diagram event handlers -------------------------------------

  private _onParametricNodeSelect(nodeId: string): void {
    this.modeler.selectNode(nodeId);
    const el = this.modeler.allElements.find((e) => e.id === nodeId);
    if (el?.source) {
      this._revealRange(el.source);
    }
  }

  private _onParametricEdgeSelect(edgeId: string): void {
    const el = this.modeler.allElements.find((e) => e.id === edgeId);
    if (el?.source) {
      this._revealRange(el.source);
    }
  }

  // -- Decomposition breadcrumbs --------------------------------------------

  private _renderDecompositionBreadcrumbs() {
    const path = this.modeler.decompositionPath;
    if (path.length <= 1) return nothing;

    return html`
      <div class="decomposition-breadcrumbs">
        ${path.map((item, index) => html`
          ${index > 0 ? html`<span class="decomposition-sep">/</span>` : nothing}
          <button
            class="decomposition-crumb ${index === path.length - 1 ? 'active' : ''}"
            @click=${() => {
              if (index < path.length - 1) {
                this.modeler.navigateToDecompositionLevel(index);
              }
            }}
            ?disabled=${index === path.length - 1}
          >${item.partDefName}</button>
        `)}
        ${this.modeler.decompositionStack.length > 0 ? html`
          <button
            class="decomposition-up"
            title="Navigate up (Alt+Up)"
            @click=${() => this.modeler.navigateUpDecomposition()}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="18 15 12 9 6 15"/></svg>
          </button>
        ` : nothing}
      </div>
    `;
  }

  // -- BDD decomposition breadcrumbs ----------------------------------------

  private _renderBDDBreadcrumbs() {
    const path = this.modeler.bddDecompositionPath;
    if (path.length === 0) return nothing;

    // Get the package name for the root breadcrumb
    const packageName = this.modeler.parsedModel?.packages?.[0]?.name ?? 'Model';

    return html`
      <div class="decomposition-breadcrumbs">
        <button
          class="decomposition-crumb"
          @click=${() => this.modeler.navigateToBDDLevel(-1)}
        >${packageName}</button>
        ${path.map((item, index) => html`
          <span class="decomposition-sep">/</span>
          <button
            class="decomposition-crumb ${index === path.length - 1 ? 'active' : ''}"
            @click=${() => {
              if (index < path.length - 1) {
                this.modeler.navigateToBDDLevel(index);
              }
            }}
            ?disabled=${index === path.length - 1}
          >${item.partDefName}</button>
        `)}
        <button
          class="decomposition-up"
          title="Navigate up (Alt+Up)"
          @click=${() => this.modeler.navigateUpBDD()}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="18 15 12 9 6 15"/></svg>
        </button>
      </div>
    `;
  }

  private _revealRange(range: { startLine: number; startColumn: number; endLine: number; endColumn: number }): void {
    const editor = this.shadowRoot?.querySelector('sysml-editor') as HTMLElement & {
      revealRange?: (startLine: number, startColumn: number, endLine?: number, endColumn?: number) => Promise<void>;
    } | null;
    editor?.revealRange?.(range.startLine, range.startColumn, range.endLine, range.endColumn);
  }

  private _onFormat(): void {
    const editor = this.shadowRoot?.querySelector('sysml-editor') as HTMLElement & { formatDocument?: () => Promise<void> } | null;
    editor?.formatDocument?.();
  }

  private _onApplySuggestion(e: CustomEvent<{ code: string }>): void {
    this._onInsertCode(e);
  }

  private _onQuickRefInsert(e: CustomEvent<{ syntax: string; example?: string }>): void {
    const text = (e.detail?.example ?? e.detail?.syntax ?? '').trim();
    if (!text) return;
    const toInsert = text.endsWith('\n') ? text : `${text}\n`;
    const editor = this.shadowRoot?.querySelector('sysml-editor') as HTMLElement & { insertText?: (text: string) => Promise<void> } | null;
    if (editor?.insertText) {
      editor.insertText(toInsert);
    } else {
      const newText = this.modeler.sourceText.trimEnd() + '\n' + toInsert;
      this.modeler.updateSourceText(newText);
    }
  }

  private _onSearchQuery(query: string, filters: Array<{ tag: string; active: boolean }>): void {
    if (!query || query.length < 2) {
      this._searchResults = [];
      return;
    }

    const elements = this.modeler.allElements;
    const activeTypes = filters.filter(f => f.active).map(f => f.tag);
    const lowerQuery = query.toLowerCase();

    const typeCategory = (et: string): string => {
      if (et === 'package') return 'package';
      if (et.includes('part')) return 'part';
      if (et.includes('port') || et.includes('item')) return 'port';
      if (et.includes('requirement') || et.includes('satisfy')) return 'requirement';
      if (et.includes('action') || et.includes('state') || et.includes('perform') || et.includes('flow')) return 'action';
      return 'attribute';
    };

    this._searchResults = elements
      .filter(el => {
        const matchesQuery = el.name.toLowerCase().includes(lowerQuery) ||
          el.qualifiedName.toLowerCase().includes(lowerQuery);
        const cat = typeCategory(el.elementType);
        const matchesType = activeTypes.length === 0 || activeTypes.includes(cat);
        return matchesQuery && matchesType;
      })
      .map((el) => {
        const nameMatch = el.name.toLowerCase() === lowerQuery;
        const startsWith = el.name.toLowerCase().startsWith(lowerQuery);
        const score = nameMatch ? 100 : startsWith ? 90 : 80 - Math.min(20, el.name.length - query.length);
        return {
          id: el.id,
          type: typeCategory(el.elementType),
          name: el.name,
          path: el.qualifiedName,
          line: el.source?.startLine,
          column: el.source?.startColumn,
          preview: `${el.elementType.replace(/([A-Z])/g, ' $1').trim()} ${el.name}`,
          score: Math.max(10, score),
          tags: [typeCategory(el.elementType)],
        };
      })
      .sort((a, b) => b.score - a.score)
      .slice(0, 20);
  }

  private _onSearchResultSelect(result: { id: string; line?: number; column?: number }): void {
    const el = this.modeler.allElements.find(e => e.id === result.id);
    if (el?.source) {
      this._revealRange(el.source);
    } else if (result.line) {
      this._revealRange({ startLine: result.line, startColumn: result.column ?? 1, endLine: result.line, endColumn: 999 });
    }
  }

  private _onInsertCode(e: CustomEvent<{ code: string; insertionPoint?: string }>): void {
    const { code, insertionPoint } = e.detail;
    if (!code) return;

    if (insertionPoint === 'cursor') {
      const editor = this.shadowRoot?.querySelector('sysml-editor') as HTMLElement & { insertText?: (text: string) => Promise<void> } | null;
      if (editor?.insertText) {
        editor.insertText(code);
        return;
      }
    }

    const src = this.modeler.sourceText;

    if (insertionPoint === 'after-imports') {
      const lines = src.split('\n');
      let lastImportLine = -1;
      for (let i = 0; i < lines.length; i++) {
        if (lines[i].trimStart().startsWith('import ')) lastImportLine = i;
      }
      if (lastImportLine >= 0) {
        lines.splice(lastImportLine + 1, 0, code);
        this.modeler.updateSourceText(lines.join('\n'));
        return;
      }
    }

    if (insertionPoint === 'inside-package') {
      const lastBrace = src.lastIndexOf('}');
      if (lastBrace > 0) {
        const before = src.slice(0, lastBrace);
        const after = src.slice(lastBrace);
        this.modeler.updateSourceText(before + '\n' + code + '\n' + after);
        return;
      }
    }

    const newText = src.trimEnd() + '\n' + code + '\n';
    this.modeler.updateSourceText(newText);
  }

  // -- Command Palette -------------------------------------------------------

  private _getPaletteCommands(): PaletteCommand[] {
    const mod = IS_MAC ? 'Cmd' : 'Ctrl';
    return [
      { id: 'save', label: 'Save Document', shortcut: `${mod}+S`, category: 'File' },
      { id: 'format', label: 'Format Document', category: 'Editor' },
      { id: 'toggle-theme', label: `Switch to ${this._theme === 'dark' ? 'Light' : 'Dark'} Theme`, category: 'View' },
      { id: 'toggle-primary-sidebar', label: 'Toggle Primary Sidebar', shortcut: `${mod}+B`, category: 'View' },
      { id: 'toggle-secondary-sidebar', label: 'Toggle Secondary Sidebar', shortcut: `${mod}+Shift+B`, category: 'View' },
      { id: 'toggle-bottom-panel', label: 'Toggle Bottom Panel', shortcut: `${mod}+J`, category: 'View' },
      { id: 'open-search', label: 'Search in Files', shortcut: `${mod}+Shift+F`, category: 'Search' },
      { id: 'split-editor', label: 'Split Editor', shortcut: `${mod}+\\`, category: 'Editor' },
      { id: 'close-tab', label: 'Close Active Tab', shortcut: `${mod}+W`, category: 'Editor' },
      { id: 'view-block', label: 'Switch to Block Diagram', category: 'Diagram' },
      { id: 'view-ibd', label: 'Switch to IBD View', category: 'Diagram' },
      { id: 'view-sequence', label: 'Switch to Sequence Diagram', category: 'Diagram' },
      { id: 'view-statemachine', label: 'Switch to State Machine', category: 'Diagram' },
      { id: 'view-activity', label: 'Switch to Activity Diagram', category: 'Diagram' },
      { id: 'view-package', label: 'Switch to Package Diagram', category: 'Diagram' },
      { id: 'view-requirement', label: 'Switch to Requirement Diagram', category: 'Diagram' },
      { id: 'view-usecase', label: 'Switch to Use Case Diagram', category: 'Diagram' },
      { id: 'view-parametric', label: 'Switch to Parametric Diagram', category: 'Diagram' },
      { id: 'auto-layout', label: 'Auto Layout Diagram', category: 'Diagram' },
      { id: 'focus-editor', label: 'Focus Editor Only', category: 'View' },
      { id: 'focus-diagram', label: 'Focus Diagram Only', category: 'View' },
      { id: 'exit-focus', label: 'Exit Focus Mode', shortcut: 'Escape', category: 'View' },
      { id: 'show-diagnostics', label: 'Show Diagnostics', category: 'Panel' },
      { id: 'show-chatbot', label: 'Show AI Assistant', category: 'Panel' },
      { id: 'show-health', label: 'Show Model Health', category: 'Panel' },
      { id: 'show-verification', label: 'Show Verification', category: 'Panel' },
      { id: 'show-requirements', label: 'Show Requirements', category: 'Panel' },
      { id: 'show-builder', label: 'Show Builder Wizard', category: 'Panel' },
      { id: 'show-quick-ref', label: 'Show Quick Reference', category: 'Panel' },
      { id: 'preset-editing', label: 'Editing Layout', category: 'Preset' },
      { id: 'preset-modeling', label: 'Modeling Layout', category: 'Preset' },
      { id: 'preset-review', label: 'Review Layout', category: 'Preset' },
      { id: 'preset-verification', label: 'Verification Layout', category: 'Preset' },
      { id: 'preset-presentation', label: 'Presentation Layout', category: 'Preset' },
    ];
  }

  private _onCommandExecute(e: CustomEvent<{ commandId: string }>): void {
    this._commandPaletteOpen = false;
    const id = e.detail.commandId;

    switch (id) {
      case 'save': this._onSave(); break;
      case 'format': this._onFormat(); break;
      case 'toggle-theme': this._onToggleTheme(); break;
      case 'toggle-primary-sidebar':
        this._updateLayout({ primarySidebarCollapsed: !this._layout.primarySidebarCollapsed }); break;
      case 'toggle-secondary-sidebar':
        this._updateLayout({ secondarySidebarCollapsed: !this._layout.secondarySidebarCollapsed }); break;
      case 'toggle-bottom-panel':
        this._updateLayout({ bottomPanelCollapsed: !this._layout.bottomPanelCollapsed }); break;
      case 'open-search':
        this._updateLayout({ bottomPanelCollapsed: false, bottomPanelActiveTab: 'search' }); break;
      case 'split-editor': this._onSplitEditor(); break;
      case 'close-tab':
        if (this.documents.activeDocumentId) {
          this._onTabClose(new CustomEvent('tab-close', { detail: { id: this.documents.activeDocumentId } }));
        }
        break;
      case 'view-block': this._updateLayout({ diagramView: 'diagram' }); break;
      case 'view-ibd':
        if (!this.modeler.activeIBDProjection) this.modeler.autoSelectIBDTarget();
        this._updateLayout({ diagramView: 'ibd' });
        break;
      case 'view-sequence': this._updateLayout({ diagramView: 'sequence' }); break;
      case 'view-statemachine': this._updateLayout({ diagramView: 'statemachine' }); break;
      case 'view-activity': this._updateLayout({ diagramView: 'activity' }); break;
      case 'view-package': this._updateLayout({ diagramView: 'package' }); break;
      case 'view-requirement': this._updateLayout({ diagramView: 'requirement' }); break;
      case 'view-usecase': this._updateLayout({ diagramView: 'usecase' }); break;
      case 'view-parametric': this._updateLayout({ diagramView: 'parametric' }); break;
      case 'auto-layout': this.modeler.autoLayout(); break;
      case 'focus-editor': this._updateLayout({ focusMode: 'editor' }); break;
      case 'focus-diagram': this._updateLayout({ focusMode: 'diagram' }); break;
      case 'exit-focus': this._updateLayout({ focusMode: 'none' }); break;
      case 'show-diagnostics':
        this._updateLayout({ bottomPanelCollapsed: false, bottomPanelActiveTab: 'diagnostics' }); break;
      case 'show-chatbot':
        this._updateLayout({ secondarySidebarCollapsed: false, secondarySidebarActiveTab: 'chatbot' }); break;
      case 'show-health':
        this._updateLayout({ secondarySidebarCollapsed: false, secondarySidebarActiveTab: 'health' }); break;
      case 'show-verification':
        this._updateLayout({ secondarySidebarCollapsed: false, secondarySidebarActiveTab: 'verification' }); break;
      case 'show-requirements':
        this._updateLayout({ secondarySidebarCollapsed: false, secondarySidebarActiveTab: 'requirements' }); break;
      case 'show-builder':
        this._updateLayout({ secondarySidebarCollapsed: false, secondarySidebarActiveTab: 'builder' }); break;
      case 'show-quick-ref':
        this._updateLayout({ secondarySidebarCollapsed: false, secondarySidebarActiveTab: 'quickRef' }); break;
      default:
        if (id.startsWith('preset-')) {
          this._applyPreset(id.replace('preset-', '') as LayoutPresetName);
        }
        break;
    }
  }

  private _applyPreset(name: LayoutPresetName): void {
    const preset = LAYOUT_PRESETS[name];
    if (!preset) return;
    this._updateLayout(preset);
    // Also update editor groups if editorSplitPct changed
    if (preset.editorSplitPct !== undefined) {
      this._editorGroups = [
        { id: 'editor', sizePct: preset.editorSplitPct },
        { id: 'diagram', sizePct: 100 - preset.editorSplitPct },
      ];
    }
  }

  // -- Breadcrumb Navigation -------------------------------------------------

  private _onBreadcrumbNavigate(e: CustomEvent<{ type: string; elementId?: string; source?: { startLine: number; startColumn: number; endLine: number; endColumn: number } }>): void {
    if (e.detail.type === 'element' && e.detail.source) {
      this._revealRange(e.detail.source);
    }
  }
}

// Note: HTMLElementTagNameMap for 'sysml-workspace' is already declared by
// @arctek/sysml-studio's Stencil-generated types. We intentionally omit a
// second declaration here to avoid TS2717 conflicts.
